import { Router } from '@angular/router';
import { Component, OnInit, inject, signal, DestroyRef, effect } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { UserService } from '../services/api/user/user.service';
import { RoutePermission } from '../enums/route-permission.enum';

interface UserLinks {
  allowedPermissions: string[];
  link: string;
  name: string;
};

@Component({
  selector: 'ccs-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false
})

export class NavbarComponent implements OnInit {
  title = 'Course Config System';
  loginDisplay = false;
  private destroyRef = inject(DestroyRef);

  private authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);

  protected signedIn = this.authService.signedIn;

  private readonly routeLinks: UserLinks[] = [
    { allowedPermissions: [RoutePermission.viewFullSynchronization], link: '/sync', name: 'Brightspace Sync' },
    { allowedPermissions: [RoutePermission.viewCourseList], link: '/course/list', name: 'Courses' },
    { allowedPermissions: [RoutePermission.auxiliarycourse], link: '/auxiliary-course/list', name: 'Auxiliary Courses' },
    { allowedPermissions: [RoutePermission.viewFacultyMappings], link: '/faculty/mapping', name: 'Faculty Mappings' },
    { allowedPermissions: [RoutePermission.contract, RoutePermission.viewContractInstructors], link: '/contract/instructor', name: 'Contract Instructors' },
    { allowedPermissions: [RoutePermission.staffRoleOverride, RoutePermission.viewStaffRoleOverrides], link: '/staff-role/overrides', name: 'Staff Role Overrides' },
    { allowedPermissions: [RoutePermission.admin], link: '/admin', name: 'Admin' },
    { allowedPermissions: [RoutePermission.viewUserlist], link: '/user', name: 'Users' }
  ];
  protected userLinks = signal<UserLinks[]>([]);

  constructor() {
    effect(() => {
      if (!this.authService.signedIn()) {
        this.userLinks.set([]);

        // Redirect user to main page if auto logout
        this.router.navigate(['/']);
      }
    });
  }

  ngOnInit(): void {
    let subscription: Subscription;
    if (this.signedIn()) {
      subscription = this.userService.getCurrentUser()
        .subscribe(
          user => {
            if (user) {
              this.userLinks.update(() => this.routeLinks.filter(
                route => {
                  return user.permissions.some(permission => route.allowedPermissions.includes(permission));
                }
              ));
            }
          }
        );
    }

    this.destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }

  handleLogin() {
    if (this.signedIn()) {
      return this.logout();
    }
    return this.login();
  }

  login() {
    this.authService.login();
  }

  logout() {
    this.authService.logout();
  }

  getLoginInfo() {
    return this.authService.getLoginInfo();
  }

}
